import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { type SchmancyMarkColor, type ToneVariant, toneColors } from '@schmancy/theme/tone'
import { css, html } from 'lit'
import { property } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js'
import '../icons/icon'

/**
 * The point mark of the tone family — a round tonal mark that carries either
 * nothing at all (a status dot) or one centred glyph (a tinted identity
 * disc). `<schmancy-badge>` is the same tone axis worn as a text pill; this
 * is the axis worn as a point, for the two roles a label pill cannot fill:
 * a glyph-free signal small enough to sit inside a row of text, and an
 * icon-only circle whose padding is its own diameter rather than a label's
 * line box.
 *
 * Size is the host's box, never a prop: `:host` sets an 8px default that any
 * `w-*`/`h-*` on the host overrides (author styles outrank `:host` at equal
 * specificity), and the glyph scales with the box through container-query
 * units — so one component spans an 8px presence dot and a 28px identity
 * disc with no size enum and no per-caller font-size override.
 *
 * A `hollow` dot paints the page ground and no boundary. That is deliberate:
 * the boundary's color is a function of what sits behind the dot, which only
 * the host knows, so the host draws it with its own `ring-*`/`border-*`
 * utility — which is exactly how `<schmancy-avatar>` renders its offline
 * status, composing this element rather than repeating it. Reach for
 * `outline` instead when the mark is present and toned but must not fill —
 * a ring in the tone over whatever ground the host already painted.
 */
@schmancyElement('schmancy-tone-dot')
export class SchmancyToneDot extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
				width: 0.5rem;
				height: 0.5rem;
				/* The glyph reads its size off this box, so a consumer resizing
				   the host resizes the glyph with it — no font-size per caller. */
				container-type: size;
				border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
			}
		`,
	]

	/**
	 * Which colour the mark carries — a tone (`'error'`), or a categorical series
	 * index (`3`) when the mark is one member of a set rather than a state. A
	 * series step has one weight, so `variant` moves nothing for it beyond
	 * `outline`/`hollow`. Ignored by `variant="hollow"`, which is the un-toned state.
	 */
	@property({ reflect: true }) color: SchmancyMarkColor = 'primary'

	/**
	 * How hard the mark presses its tone. `solid` is the default because a
	 * glyph-free dot is often 8px, where only the saturated ground reads;
	 * `soft` is the tinted carrier an `icon` sits on. `pinned` is the filled
	 * AREA's rung and is rarely a dot's: at this size the ground it does not
	 * flip is the one a dark surface swallows.
	 */
	@property({ type: String, reflect: true }) variant: ToneVariant = 'solid'

	/** Material Symbols glyph name. Empty renders a plain dot. */
	@property({ type: String }) icon = ''

	/**
	 * The mark's accessible name — what the color means in words. A status
	 * signalled by color alone is unreadable without it; leave it empty only
	 * when adjacent text already names the same state, in which case the mark
	 * is decorative and is hidden from assistive technology.
	 */
	@property({ type: String }) label = ''

	render() {
		// `toneColors` names a hairline's colour and never its width or style, so
		// the edge is drawn wherever the map named one — `outline` by name would
		// leave `pinned` an unbounded fill.
		const toned = toneColors(this.color, this.variant)
		return html`
			<div
				role=${this.label ? 'img' : 'presentation'}
				aria-label=${this.label}
				aria-hidden=${this.label ? 'false' : 'true'}
				class="${this.classMap({
					'w-full h-full rounded-full flex items-center justify-center': true,
					'transition-colors duration-200': true,
					'border border-solid': toned.borderColor !== undefined,
				})}"
				style="${this.styleMap(toned)}"
			>
				${when(!!this.icon, () => html`<schmancy-icon class="text-[58cqmin]">${this.icon}</schmancy-icon>`)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-tone-dot': SchmancyToneDot
	}
}
